import { StyleSheet } from 'aphrodite';
import { THEME } from '../../../../../web/components-v2/Theme/theme';
import { shadowMd, shadowSm } from '../../../../../web/styles/shadows';

// Horizontal inset from the Event Log Figma spec. It falls outside the THEME spacing scale, so it's
// single-sourced here to keep the card header, filter bar, rows, drawer, and empty state aligned.
const CARD_INSET_X = 28;

export const styleSheet = StyleSheet.create({
	container: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.md,
		padding: THEME.spacing.md,
	},
	card: {
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: 14,
		boxShadow: shadowMd,
		overflow: 'hidden',
	},
	cardHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		padding: `22px ${CARD_INSET_X}px`,
		borderBottom: THEME.common.border,
	},
	filterBar: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: 18,
		alignItems: 'flex-end',
		padding: `22px ${CARD_INSET_X}px`,
	},
	filterField: {
		display: 'flex',
		flexDirection: 'column',
		gap: 7,
		minWidth: 180,
	},
	// The contact search placeholder ("Search by contact name") needs more room than the
	// dropdowns, so give it a wider minimum so the hint text isn't clipped.
	contactField: {
		minWidth: 240,
	},
	filterLabel: {
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.light,
	},
	retentionNote: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
		marginLeft: 'auto',
	},
	dateRangeNote: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
		padding: `${THEME.spacing.xs}px ${THEME.spacing.md}px`,
	},
	columnHeaderRow: {
		backgroundColor: THEME.colors.background.main,
	},
	columnHeader: {
		textAlign: 'left',
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.light,
		textTransform: 'uppercase',
		letterSpacing: '0.06em',
		padding: `13px ${THEME.spacing.sm}px`,
		borderBottom: THEME.common.border,
		':nth-child(1)': { width: 168 },
		':nth-child(2)': { width: 200 },
		':nth-child(3)': { width: 120 },
		':nth-child(5)': { width: 28 },
	},
	firstColumn: {
		paddingLeft: CARD_INSET_X,
	},
	lastColumn: {
		paddingRight: CARD_INSET_X,
	},
	table: {
		width: '100%',
		borderCollapse: 'collapse',
		// On narrow screens, switch to a fixed layout so column widths are bounded — this is
		// what lets the data cells ellipsis (see `truncatedCell`) instead of overflowing.
		[THEME.queries.sm]: {
			tableLayout: 'fixed',
		},
	},
	row: {
		borderBottom: THEME.common.border,
	},
	// Only rows that have expandable details get the clickable affordance.
	rowInteractive: {
		cursor: 'pointer',
		':hover': {
			backgroundColor: THEME.colors.hover.light,
		},
	},
	// Trailing cell that holds only the expand chevron — fixed to the design's 28px column.
	chevronCell: {
		width: 28,
		padding: THEME.spacing.xs,
		verticalAlign: 'middle',
	},
	expandButton: {
		flexShrink: 0,
		width: 20,
		height: 20,
		padding: 0,
	},
	expandChevron: {
		width: 14,
		height: 14,
		transition: 'transform 0.15s ease',
	},
	expandChevronOpen: {
		transform: 'rotate(180deg)',
	},
	cell: {
		padding: `18px ${THEME.spacing.sm}px`,
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.main,
		verticalAlign: 'top',
	},
	// Applied to the four data cells (not the expandable details cell): on small screens long
	// values are truncated with an ellipsis rather than wrapping or overflowing the viewport.
	truncatedCell: {
		[THEME.queries.sm]: {
			...THEME.common.truncate,
		},
	},
	rowExpanded: {
		backgroundColor: THEME.colors.hover.light,
	},
	// Actor cell: avatar + name + optional Levitate badge in a flex row.
	actorCell: {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
	},
	avatar: {
		width: 34,
		height: 34,
		flexShrink: 0,
		fontSize: THEME.typography.fontSizes.sm,
	},
	actorName: {
		...THEME.common.truncate,
	},
	detailsRow: {
		backgroundColor: THEME.colors.hover.light,
	},
	detailsDrawer: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
		padding: `18px ${CARD_INSET_X}px`,
	},
	detailsGroup: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
	},
	detailsGroupLabel: {
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.light,
		textTransform: 'uppercase',
		letterSpacing: '0.05em',
	},
	detailsChips: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: THEME.spacing.xs,
	},
	// Chip for individual changed-field keys.
	fieldChip: {
		display: 'inline-block',
		padding: '4px 9px',
		borderRadius: 6,
		backgroundColor: THEME.colors.secondary.light,
		color: THEME.colors.secondary.dark,
		fontFamily: THEME.typography.fontFamilies.lexend,
		fontSize: THEME.typography.fontSizes.xs,
	},
	detailsMeta: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: THEME.spacing.md,
	},
	detailsMetaItem: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
	},
	detailsMetaLabel: {
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.light,
		textTransform: 'uppercase',
		letterSpacing: '0.05em',
	},
	detailsMetaValue: {
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.main,
		wordBreak: 'break-all',
	},
	stateMessage: {
		padding: THEME.spacing.lg,
		textAlign: 'center',
		color: THEME.colors.text.light,
	},
	// Loading variant of stateMessage: centers the spinner alongside the label on one row.
	loadingState: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: THEME.spacing.sm,
	},
	emptyState: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: THEME.spacing.sm,
		padding: `70px ${CARD_INSET_X}px`,
		color: THEME.colors.text.light,
	},
	emptyStateIcon: {
		width: 52,
		height: 52,
		color: THEME.colors.icon.gray,
	},
	emptyStateHeading: {
		margin: 0,
		fontSize: THEME.typography.fontSizes.md,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.main,
	},
	emptyStateSubtext: {
		margin: 0,
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.light,
	},
	loadMoreWrapper: {
		display: 'flex',
		justifyContent: 'center',
		padding: THEME.spacing.md,
	},
	// Inline spinner + label shown in the contact dropdown's status row while a search is in flight.
	contactStatus: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.xs,
	},
	// Selected-contact badge, styled to match the adjacent filter dropdowns (36px tall, bordered).
	selectedContact: {
		boxSizing: 'border-box',
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		height: 36,
		width: 'fit-content',
		maxWidth: '100%',
		padding: `${THEME.spacing.xs}px ${THEME.spacing.md}px`,
		borderRadius: THEME.common.borderRadius,
		border: THEME.common.border,
		backgroundColor: 'transparent',
		boxShadow: shadowSm,
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.main,
	},
	selectedContactAvatar: {
		width: 20,
		height: 20,
		flexShrink: 0,
	},
	selectedContactName: {
		...THEME.common.truncate,
	},
	selectedContactRemove: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		padding: 0,
		border: 'none',
		background: 'transparent',
		cursor: 'pointer',
		color: THEME.colors.icon.gray,
		':hover': {
			color: THEME.colors.text.main,
		},
	},
	contactDropdown: {
		// Size the dropdown to the widest contact row instead of the input width (the shared popup
		// pins width to var(--anchor-width)). Never go narrower than the input; the popup's own
		// maxWidth: var(--available-width) keeps it from running off-screen.
		width: 'max-content',
		minWidth: 'var(--anchor-width)',
	},
	// Action pill: fully-rounded shape with a leading colored dot.
	actionPill: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		padding: '5px 11px 5px 9px',
		borderRadius: 999,
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	actionPillGreen: {
		backgroundColor: THEME.colors.success.light,
		color: THEME.colors.success.dark,
	},
	actionPillBlue: {
		backgroundColor: THEME.colors.primary.light,
		color: THEME.colors.primary.dark,
	},
	actionPillGray: {
		backgroundColor: THEME.colors.background.dark,
		color: THEME.colors.text.light,
	},
	actionDot: {
		width: 6,
		height: 6,
		borderRadius: '50%',
		flexShrink: 0,
	},
	actionDotGreen: {
		backgroundColor: THEME.colors.success.dark,
	},
	actionDotBlue: {
		backgroundColor: THEME.colors.primary.dark,
	},
	actionDotGray: {
		backgroundColor: THEME.colors.text.light,
	},
	// Levitate support badge — tiny uppercase warning pill.
	levitateBadge: {
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.bold,
		textTransform: 'uppercase',
		letterSpacing: '0.04em',
		color: THEME.colors.warning.dark,
		backgroundColor: THEME.colors.warning.light,
		padding: '2px 7px',
		borderRadius: 999,
	},
});
